import React, { useState } from 'react';
import './Pages/PagesCss/Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: 'general',
        message: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 2000);
  };

  const contactMethods = [
    {
      icon: '📧',
      title: 'Email Us',
      description: 'Send us an email anytime',
      details: 'support@creditsiq.com',
      link: 'mailto:support@creditsiq.com'
    },
    {
      icon: '📍',
      title: 'Visit Us',
      description: 'Our headquarters',
      details: '2568 Madison Ave, League City, TX 77573, USA',
      link: 'https://maps.google.com'
    }
  ];

  const faqs = [
    {
      question: 'How secure is my financial information?',
      answer: 'We use bank-level 256-bit SSL encryption and never store your sensitive banking credentials. All data is anonymized and protected.'
    },
    {
      question: 'Can I get personalized credit advice?',
      answer: 'Yes! Our credit specialists can provide personalized recommendations based on your unique financial situation and goals.'
    },
    {
      question: 'Do you offer business solutions?',
      answer: 'Absolutely. We have dedicated solutions for small businesses and enterprises. Contact our business team for more information.'
    },
    {
      question: 'How quickly will I get a response?',
      answer: 'We typically respond within 2 hours during business hours, and within 24 hours maximum for all inquiries.'
    }
  ];

  return (
    <div className="contact">
      {/* Header Section */}
      <section className="contact-header">
        <div className="container">
          <div className="header-content">
            <h1 className="header-title">Get in Touch</h1>
            <p className="header-description">
              Have questions about CreditsIQ? We're here to help you achieve your financial goals. 
              Reach out to our team of experts.
            </p>
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="contact-main">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="form-header">
                <h2>Send us a Message</h2>
                <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              </div>

              {submitStatus === 'success' && (
                <div className="success-message">
                  <div className="success-icon">✓</div>
                  <div className="success-content">
                    <h3>Message Sent Successfully!</h3>
                    <p>Thank you for contacting us. We'll get back to you within 24 hours.</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="subject" className="form-label">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="What is this regarding?"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="category" className="form-label">
                      Category *
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="general">General Inquiry</option>
                      <option value="technical">Technical Support</option>
                      <option value="partnership">Partnership</option>
                      <option value="feedback">Feedback & Suggestions</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="form-textarea"
                    placeholder="Please describe your inquiry in detail..."
                    rows="6"
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className={`submit-button ${isSubmitting ? 'submitting' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner"></div>
                      Sending Message...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-section">
              <div className="info-card">
                <h3>Contact Information</h3>
                <p className="info-description">
                  Choose the most convenient way to reach us. Our support team is always ready to assist you.
                </p>

                <div className="contact-methods">
                  {contactMethods.map((method, index) => (
                    <a 
                      key={index} 
                      href={method.link} 
                      className="contact-method"
                      target={method.link.startsWith('http') ? '_blank' : '_self'}
                      rel={method.link.startsWith('http') ? 'noopener noreferrer' : ''}
                    >
                      <div className="method-icon">{method.icon}</div>
                      <div className="method-content">
                        <h4 className="method-title">{method.title}</h4>
                        <p className="method-description">{method.description}</p>
                        <span className="method-details">{method.details}</span>
                      </div>
                      <div className="method-arrow">→</div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
            <p>Quick answers to common questions about CreditsIQ</p>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Improve Your Financial Health?</h2>
            <p>Join thousands of users who trust CreditsIQ for their financial journey.</p>
            <div className="cta-buttons">
              <button className="btn btn-primary">Explore Free Tools</button>
              <button className="btn btn-secondary">Learn More</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;